'use client';

import type { ReactNode } from 'react';
import { useState, useCallback, useMemo } from 'react';
import { Link, usePathname } from 'shared/lib/i18n';
import mergeClasses from 'shared/lib/utils';
import { useSidebarEffect } from 'shared/hooks/useSidebarEffect';
import { Drawer, DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle, DrawerTrigger } from 'shared/ui/drawer';
import { useIsMobile } from 'shared/hooks/use-mobile';
import { ChevronUp } from 'lucide-react';
import { Button } from 'shared/ui/button';
interface SecondarySidebarProps {
  sidebarName: string | ReactNode;
  sidebarItems: {
    name: string;
    path: string;
  }[];
  showHeader?: boolean;
}
export const SecondarySidebar = ({
  sidebarName,
  sidebarItems,
  showHeader = false
}: SecondarySidebarProps) => {
  const pathname = usePathname();
  const isMobile = useIsMobile(641);
  const [isOpen, setIsOpen] = useState(false);
  useSidebarEffect(false);
  const sortedPathByLength = useMemo(() => {
    return [...sidebarItems].sort((a, b) => b.path.length - a.path.length); // Sort by length to prioritize longer paths
  }, [sidebarItems]);
  const constPath = useCallback((path: string) => {
    return pathname.startsWith(path);
  }, [pathname]);
  const basePath = useMemo(() => {
    return sortedPathByLength.find(({
      path
    }) => constPath(path))?.path;
  }, [constPath, sortedPathByLength]);
  if (isMobile) return <nav>
        <Drawer open={isOpen} onOpenChange={setIsOpen}>
          <DrawerTrigger asChild>
            <Button className="fixed bottom-0 left-1/2 -translate-x-1/2 z-10 rounded-none bg-primary/70 backdrop-blur-sm rounded-t-lg space-x-1" size="sm" onClick={() => setIsOpen(true)}>
              <ChevronUp /> <span>Меню проекта</span>
            </Button>
          </DrawerTrigger>
          <DrawerContent>
            <DrawerHeader>
              <DrawerTitle>Навигация по проекту</DrawerTitle>
              <DrawerDescription>
                <ul className="flex flex-col ps-0 text-body2 mt-2 space-y-1">
                  {sidebarItems.map(({
                name,
                path
              }) => <li key={path}>
                      <Link onClick={() => setIsOpen(false)} key={path} href={path} scroll replace className={mergeClasses('py-2 px-4 block w-full hover:underline', basePath === path ? 'bg-primary font-medium text-accent hover:no-underline rounded-xl' : '')}>
                        {name}
                      </Link>
                    </li>)}
                </ul>
              </DrawerDescription>
            </DrawerHeader>
          </DrawerContent>
        </Drawer>
      </nav>;
  return <aside className="hidden sm:block h-full border border-white dark:border-black border-r-secondary bg-secondary" data-sentry-component="SecondarySidebar" data-sentry-source-file="secondary-sidebar.tsx">
      <nav className={mergeClasses('flex h-full  flex-col text-body3', showHeader ? 'w-[200px]' : 'w-[170px]')}>
        <div className="flex flex-1 flex-col h-full">
          {showHeader && <h1 className="h-9 text-title4 leading-[17.15px] border-b mb-2 truncate flex items-center mt-2 px-4">
              {sidebarName}
            </h1>}

          <ul className="flex flex-col ps-0 text-body2">
            {sidebarItems.map(({
            name,
            path
          }) => <li key={path} className="gap-2">
                <Link key={path} href={path} scroll replace className={mergeClasses('py-2 px-4 block w-full hover:underline', basePath === path ? 'text-primary bg-background hover:no-underline' : '')}>
                  {name}
                </Link>
              </li>)}
          </ul>
        </div>
      </nav>
    </aside>;
};